import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import OnboardingWizard from "@/components/OnboardingWizard";
import ProfileMetrics from "@/components/ProfileMetrics";
import ComplianceConsentModal from "@/components/compliance/ComplianceConsentModal";
import { useConsentStatus } from "@/lib/compliance";
import axios from "axios";
import { trackTrialStarted } from "@/utils/analytics";
import RegisterProgressStepper from "@/components/RegisterProgressStepper";
import RegisterCompleteScreen from "@/components/RegisterCompleteScreen";
import CycleOnboardingModal, { shouldShowCycleOnboarding } from "@/components/CycleOnboardingModal";

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, API, refreshUser } = useAuth();
  const [completedProfile, setCompletedProfile] = useState(null);
  const [showResults, setShowResults] = useState(false);
  // iter1097 — финална celebration screen преди Dashboard redirect (2s).
  const [showComplete, setShowComplete] = useState(false);
  // iter1097 / iter1098 — Cycle Modal тук е за Google OAuth users, които
  // заобикалят Register-a. Отваря се СЛЕД wizard-a (когато gender е известен),
  // не на mount (profile е null за нови Google users → shouldShowCycleOnboarding
  // би върнало false).
  const [cycleModalOpen, setCycleModalOpen] = useState(false);
  const [cycleModalHandled, setCycleModalHandled] = useState(false);
  const { status: consentStatus, loading: consentLoading, refresh: refreshConsent } = useConsentStatus();
  const needsConsent = !consentLoading && consentStatus && consentStatus.up_to_date === false;

  // Feb 2026 — `?force=1` (or `?rerun=1`) query param lets an admin, clinician,
  // or patient re-run the wizard even if they've completed it once. The
  // completedOnboarding flag stays set — this is only a UI bypass.
  const forceRerun = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("force") === "1" || params.get("rerun") === "1";
  }, [location.search]);

  const genderRaw = user?.profile?.personal?.gender;
  const skipCycleInStepper = !!(genderRaw && !String(genderRaw).toLowerCase().startsWith("f") && String(genderRaw).toLowerCase() !== "жена");

  // If already completed onboarding (and not showing results), redirect
  useEffect(() => {
    if (user?.profile?.completedOnboarding && !showResults && !showComplete && !forceRerun) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, showResults, showComplete, navigate, forceRerun]);

  const handleComplete = (profileData) => {
    setCompletedProfile(profileData);
    setShowResults(true);
  };

  const handleContinue = async () => {
    // If we came from SymptomAssessment, start the trial automatically
    const assessmentId = location.state?.assessmentId;
    if (assessmentId) {
      try {
        await axios.post(`${API}/trial/start`, { assessment_id: assessmentId });
        trackTrialStarted("core", 7);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error("Failed to start trial:", e);
      }
    }

    // iter1098 — Ако user е жена без LMP (типично за Google OAuth signup,
    // където profile е null на mount) → отваряме Cycle Modal ТУК, СЛЕД като
    // wizard-a е събрал gender. Complete screen се показва след като модалът
    // е dismiss-нат / submit-нат.
    if (!cycleModalHandled) {
      try {
        // Refresh user data — wizard току-що записа gender + други полета.
        if (refreshUser) await refreshUser();
      } catch { /* noop */ }
      // Read fresh user from context on next tick via effect гарантирано би
      // било сложно; използваме локалния state ако е налично, иначе context.
      // След refreshUser() context user-a e updated.
      // NOTE: user prop идва от closure — но React ще re-render след refreshUser.
      // За да гарантираме fresh check, използваме axios /auth/me директно.
      let fresh = user;
      try {
        const token = sessionStorage.getItem("nutramilo_access_token") || localStorage.getItem("nutramilo_access_token");
        const me = await axios.get(`${API}/auth/me`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        fresh = me.data;
      } catch { /* fallback to closure user */ }
      setCycleModalHandled(true);
      if (shouldShowCycleOnboarding(fresh)) {
        setCycleModalOpen(true);
        return; // Complete screen показваме на modal close.
      }
    }

    // iter1097 — вместо direct navigate → показваме 2s celebration screen.
    setShowResults(false);
    setShowComplete(true);
  };

  // iter1098 — Cycle Modal close/submit callback: продължаваме към Complete screen.
  const handleCycleModalDone = () => {
    setCycleModalOpen(false);
    setShowResults(false);
    setShowComplete(true);
  };

  // iter1097 — финална Step 4 „Готов" celebration → Dashboard.
  if (showComplete) {
    return (
      <RegisterCompleteScreen
        skipCycle={skipCycleInStepper}
        onDone={() => navigate("/dashboard", { replace: true })}
      />
    );
  }

  if (showResults && completedProfile) {
    return (
      <>
        {/* iter1098 — Cycle Modal се показва OVER ProfileMetrics-a ако user
            е жена без LMP (типичен Google OAuth случай). onOpenChange
            гарантира че и dismiss, и submit водят към Complete screen. */}
        <CycleOnboardingModal
          open={cycleModalOpen}
          onOpenChange={(v) => {
            if (!v) {
              handleCycleModalDone();
            } else {
              setCycleModalOpen(v);
            }
          }}
          onComplete={handleCycleModalDone}
          stepperSlot={<RegisterProgressStepper currentStep={2} variant="compact" />}
        />
        <ProfileMetrics 
          profile={{
            metrics: completedProfile.metrics,
            formData: completedProfile
          }} 
          onContinue={handleContinue} 
        />
      </>
    );
  }

  return (
    <>
      {/* Gate the wizard behind the GDPR/consent modal — user must accept
          terms before filling in any health data. Modal is force-open while
          status.up_to_date is false. refreshConsent() re-checks after accept
          so `force` releases the wizard. */}
      <ComplianceConsentModal
        force={needsConsent}
        onClose={() => { refreshConsent(); }}
      />
      <div className={needsConsent ? "pointer-events-none opacity-40 blur-sm" : ""} aria-hidden={needsConsent}>
        {/* iter1096 — Progress stepper: сме на стъпка 3 (Assessment). Cycle
            wizard-a е зад нас (submit или skip). Показваме го само за прясно
            регистрирани — ако user-а прави force=rerun, скриваме stepper-a. */}
        {!forceRerun && (
          <div className="max-w-3xl mx-auto pt-4 px-4">
            <RegisterProgressStepper
              currentStep={3}
              skipCycle={skipCycleInStepper}
            />
          </div>
        )}
        <OnboardingWizard onComplete={handleComplete} />
      </div>
    </>
  );
}
